import type {
  AgentSuggestion,
  BuildFromSelectionsScheduleDay,
  ParseEmailExtracted,
} from '@travel-planner/shared/schemas';
import { Info } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { Alert, Badge, Button, Heading, Text } from '@/shared/ui';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import {
  flattenSchedule,
  useGeneratorCommit,
} from '../../model/use-generator-commit';
import css from './budget-step.module.scss';
import { buildProductIndex, formatCents } from './slot-pricing';

const styles = pickCss(css, css.root);

// Buckets shown in the breakdown, in render order. Each bucket maps to a
// set of `category_enum` values from the schedule slot's contentCategory.
// Activities collapses Activity + Attraction so the bucket counts match
// the resolver's grouping (`itinerary-category-options.test.ts:157`).
const BUCKETS = [
  {
    key: 'stay',
    label: 'Stay',
    categories: new Set(['Accommodation']),
    color: 'var(--color-primary)',
  },
  {
    key: 'activities',
    label: 'Activities',
    categories: new Set(['Activity', 'Attraction']),
    color: 'var(--color-success)',
  },
  {
    key: 'dining',
    label: 'Dining',
    categories: new Set(['Dining']),
    color: 'var(--color-warning)',
  },
  {
    key: 'transport',
    label: 'Transport',
    categories: new Set(['Transportation']),
    color: 'var(--color-badge-purple-bg)',
  },
] as const;

type BucketKey = (typeof BUCKETS)[number]['key'];

type BucketTotal = {
  cents: number;
  count: number;
};

const emptyTotals = (): Record<BucketKey, BucketTotal> => ({
  stay: { cents: 0, count: 0 },
  activities: { cents: 0, count: 0 },
  dining: { cents: 0, count: 0 },
  transport: { cents: 0, count: 0 },
});

type Breakdown =
  | {
      kind: 'amount';
      currency: string;
      totals: Record<BucketKey, BucketTotal>;
      totalCents: number;
      totalItems: number;
    }
  | {
      kind: 'mixed';
      totals: Record<BucketKey, BucketTotal>;
      totalItems: number;
    }
  | { kind: 'unknown' };

const computeBreakdown = (
  schedule: ReadonlyArray<BuildFromSelectionsScheduleDay>,
  index: Map<string, AgentSuggestion>
): Breakdown => {
  const totals = emptyTotals();
  let currency: string | null = null;
  let mixed = false;
  let totalItems = 0;
  let anyKnown = false;
  for (const day of schedule) {
    for (const slot of day.slots) {
      totalItems += 1;
      const bucket = BUCKETS.find((b) =>
        b.categories.has(slot.contentCategory)
      );
      if (!bucket) continue;
      const product = index.get(slot.productId);
      if (!product || product.priceCents == null || product.currency == null) {
        // Track count even when price is missing so the row reflects picks.
        totals[bucket.key].count += 1;
        continue;
      }
      anyKnown = true;
      totals[bucket.key].count += 1;
      totals[bucket.key].cents += product.priceCents;
      if (currency == null) currency = product.currency;
      else if (currency !== product.currency) mixed = true;
    }
  }
  if (mixed) return { kind: 'mixed', totals, totalItems };
  if (!anyKnown || currency == null) return { kind: 'unknown' };
  const totalCents = (Object.values(totals) as BucketTotal[]).reduce(
    (sum, b) => sum + b.cents,
    0
  );
  return { kind: 'amount', currency, totals, totalCents, totalItems };
};

const formatPercent = (numerator: number, denominator: number): string => {
  if (denominator <= 0) return '0%';
  const pct = Math.round((numerator / denominator) * 100);
  return `${pct}%`;
};

// Integer percents so the segment widths match the per-row labels and the
// values are stable for snapshot assertions. The four buckets won't always
// add up to exactly 100 after rounding (off-by-one is fine for a relative
// flex-grow weight), but they reflect what the consultant sees in the
// category list below the bar.
const computePercents = (
  totals: Record<BucketKey, BucketTotal>,
  totalCents: number
): Record<BucketKey, number> => {
  if (totalCents <= 0) {
    return { stay: 0, activities: 0, dining: 0, transport: 0 };
  }
  return {
    stay: Math.round((totals.stay.cents / totalCents) * 100),
    activities: Math.round((totals.activities.cents / totalCents) * 100),
    dining: Math.round((totals.dining.cents / totalCents) * 100),
    transport: Math.round((totals.transport.cents / totalCents) * 100),
  };
};

const adultCount = (extracted: ParseEmailExtracted): number => {
  const fromBreakdown = extracted.travellersBreakdown
    .filter((row) => row.ageBand === 'adult')
    .reduce((sum, row) => sum + row.count, 0);
  if (fromBreakdown > 0) return fromBreakdown;
  return extracted.travellersTotal ?? 0;
};

type BudgetStatus =
  | { kind: 'within'; remainingCents: number; currency: string }
  | { kind: 'over'; overCents: number; currency: string }
  | { kind: 'unknown' };

const compareBudget = (
  totalCents: number,
  totalCurrency: string,
  extracted: ParseEmailExtracted
): BudgetStatus => {
  const briefBudget = extracted.budgetTotal;
  if (!briefBudget) return { kind: 'unknown' };
  if (briefBudget.currency !== totalCurrency) return { kind: 'unknown' };
  const briefCents = Math.round(briefBudget.amount * 100);
  if (totalCents > briefCents) {
    return {
      kind: 'over',
      overCents: totalCents - briefCents,
      currency: totalCurrency,
    };
  }
  return {
    kind: 'within',
    remainingCents: briefCents - totalCents,
    currency: totalCurrency,
  };
};

export const BudgetStep = () => {
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const setGeneratorState = useAgentPanelStore((s) => s.setGeneratorState);
  const commit = useGeneratorCommit();
  const isBudgetPhase = generatorState?.phase === 'budget';

  const breakdown = useMemo<Breakdown>(() => {
    if (!isBudgetPhase) return { kind: 'unknown' };
    const index = buildProductIndex(generatorState.options);
    return computeBreakdown(generatorState.schedule, index);
  }, [isBudgetPhase, generatorState]);

  const handleBack = useCallback(() => {
    if (!isBudgetPhase) return;
    setGeneratorState({ ...generatorState, phase: 'preview' });
  }, [isBudgetPhase, generatorState, setGeneratorState]);

  const handleAddToItinerary = useCallback(() => {
    if (!isBudgetPhase) return;
    void commit(
      flattenSchedule(generatorState.schedule),
      generatorState.versionId
    );
  }, [isBudgetPhase, generatorState, commit]);

  if (!isBudgetPhase) return null;

  const { extracted, durationDays, options, selections } = generatorState;
  const adults = adultCount(extracted);
  const totals =
    breakdown.kind === 'amount' || breakdown.kind === 'mixed'
      ? breakdown.totals
      : emptyTotals();
  const percents =
    breakdown.kind === 'amount'
      ? computePercents(totals, breakdown.totalCents)
      : { stay: 0, activities: 0, dining: 0, transport: 0 };
  const totalLabel =
    breakdown.kind === 'amount'
      ? formatCents(breakdown.totalCents, breakdown.currency)
      : '—';

  // Design `bg-total-pp`: "€9,300 per traveller · 4 adults". Per-traveller
  // is computed off the adults count (we don't price children separately
  // in the brief). Drop the per-day variant from the prior implementation
  // — design only carries the per-traveller line.
  const subline =
    breakdown.kind === 'amount' && adults > 0
      ? `${formatCents(Math.round(breakdown.totalCents / adults), breakdown.currency)} per traveller · ${adults} ${adults === 1 ? 'adult' : 'adults'}`
      : null;

  // Per-row subtitle copy. Stay surfaces nights + chosen accommodation
  // title (the accommodation tab is single-select so one row maps to one
  // product). The other buckets stick to a count-derived noun.
  const stayCount = totals.stay.count;
  const activitiesCount = totals.activities.count;
  const diningCount = totals.dining.count;
  const transportCount = totals.transport.count;
  const stayProduct = options.accommodation.find(
    (p) => p.id === selections.accommodationId
  );
  const stayNights = Math.max(0, durationDays - 1);
  const stayContext = (() => {
    const parts: string[] = [];
    if (stayNights > 0) {
      parts.push(`${stayNights} ${stayNights === 1 ? 'night' : 'nights'}`);
    }
    if (stayProduct?.title) parts.push(stayProduct.title);
    return parts.join(' · ');
  })();
  const subtitleByBucket: Record<BucketKey, string> = {
    stay: stayContext || (stayCount > 0 ? `${stayCount} stays` : ''),
    activities:
      activitiesCount > 0
        ? `${activitiesCount} ${activitiesCount === 1 ? 'experience' : 'experiences'}`
        : '',
    dining:
      diningCount > 0
        ? `${diningCount} ${diningCount === 1 ? 'meal' : 'meals'}`
        : '',
    transport:
      transportCount > 0
        ? `${transportCount} ${transportCount === 1 ? 'transfer' : 'transfers'}`
        : '',
  };

  const status =
    breakdown.kind === 'amount'
      ? compareBudget(breakdown.totalCents, breakdown.currency, extracted)
      : { kind: 'unknown' as const };

  const calculatorNote =
    status.kind === 'within'
      ? `Within budget — keeps ${formatCents(status.remainingCents, status.currency)} in reserve.`
      : status.kind === 'over'
        ? `Over budget by ${formatCents(status.overCents, status.currency)} — trim a bucket to align.`
        : breakdown.kind === 'mixed'
          ? 'Totals span multiple currencies — convert before comparing to the brief.'
          : 'Budget unavailable — pick products with prices to see a comparison.';
  const rationale = [...extracted.assumptions, calculatorNote]
    .filter((part) => part.length > 0)
    .join(' ');

  return (
    <div className={styles()}>
      <section aria-label="Budget total" className={css.totalCard}>
        <div className={css.totalAmounts}>
          <Text size="xs" variant="muted" weight="semibold">
            ESTIMATED TOTAL
          </Text>
          <Heading as="h4" size="xl">
            {totalLabel}
          </Heading>
          {subline && (
            <Text size="sm" variant="muted">
              {subline}
            </Text>
          )}
        </div>
        {status.kind === 'within' && (
          <Badge variant="active">Within budget</Badge>
        )}
        {status.kind === 'over' && <Badge variant="error">Over budget</Badge>}
        {breakdown.kind === 'mixed' && (
          <Badge variant="inactive">Mixed currencies</Badge>
        )}
        {breakdown.kind === 'unknown' && (
          <Badge variant="inactive">No totals</Badge>
        )}
      </section>

      <div aria-label="Spending split" className={css.barRow} role="img">
        {BUCKETS.map((bucket) => {
          const pct = percents[bucket.key];
          if (pct <= 0) return null;
          const pctLabel =
            breakdown.kind === 'amount'
              ? formatPercent(totals[bucket.key].cents, breakdown.totalCents)
              : '';
          return (
            <div
              className={css.barSegment}
              data-bucket={bucket.key}
              key={bucket.key}
              style={{ background: bucket.color, flexGrow: pct }}
              title={`${bucket.label} ${pctLabel}`}
            />
          );
        })}
        {breakdown.kind !== 'amount' && (
          <div aria-hidden="true" className={css.barEmpty} />
        )}
      </div>

      <ul aria-label="Category totals" className={css.categoryList}>
        {BUCKETS.map((bucket) => {
          const total = totals[bucket.key];
          const pctLabel =
            breakdown.kind === 'amount'
              ? formatPercent(total.cents, breakdown.totalCents)
              : '—';
          const amountLabel =
            breakdown.kind === 'amount'
              ? formatCents(total.cents, breakdown.currency)
              : '—';
          const subtitle = subtitleByBucket[bucket.key];
          return (
            <li className={css.categoryRow} key={bucket.key}>
              <span
                aria-hidden="true"
                className={css.dot}
                style={{ background: bucket.color }}
              />
              <Text size="sm" weight="medium">
                {bucket.label}
              </Text>
              {subtitle && (
                <Text className={css.rowSubtitle} size="xs" variant="muted">
                  {subtitle}
                </Text>
              )}
              <Text className={css.rowPct} size="xs" variant="muted">
                {pctLabel}
              </Text>
              <Text size="sm" weight="medium">
                {amountLabel}
              </Text>
            </li>
          );
        })}
      </ul>

      {rationale.length > 0 && (
        <Alert icon={<Info aria-hidden="true" size={14} />} variant="info">
          {rationale}
        </Alert>
      )}

      <footer className={css.footer}>
        <Button
          onClick={handleBack}
          size="default"
          type="button"
          variant="outline"
        >
          Back to schedule
        </Button>
        <Button
          onClick={handleAddToItinerary}
          size="default"
          type="button"
          variant="primary"
        >
          Add to itinerary
        </Button>
      </footer>
    </div>
  );
};
